import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ChevronDown, ChevronRight, Edit3, X, ReceiptText, MessageSquareOff } from "lucide-react";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";
import { formatDateTime } from "@/lib/datetime";
import TablePager from "@/components/TablePager";

const money = (n) => formatINR(n);
const STATUS_OPTIONS = ["UNPAID", "PARTIALLY_PAID", "PAID"];

const ExpandIcon = ({ expanded }) =>
  expanded ? <ChevronDown className="h-4 w-4 text-indigo-500" /> : <ChevronRight className="h-4 w-4 text-indigo-500" />;

export default function Invoices() {
  const { user } = useAuth();
  const canEdit = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterStaff, setFilterStaff] = useState("ALL");
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editStatus, setEditStatus] = useState("");
  const [editGrandTotal, setEditGrandTotal] = useState("");
  const [editSubtotal, setEditSubtotal] = useState("");
  const [editDiscount, setEditDiscount] = useState("");
  const [originalItems, setOriginalItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const load = async () => {
    try {
      const [r, s, st] = await Promise.all([
        api.get("/invoices"),
        api.get("/services"),
        api.get("/staff"),
      ]);
      setRows(r.data);
      setServices(s.data);
      setStaff(st.data);
    } catch { toast.error("Failed to load invoices"); }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = rows.filter((inv) => {
    if (filterCustomer) {
      const name = (inv.customerName || "").toLowerCase();
      if (!name.includes(filterCustomer.toLowerCase())) return false;
    }
    if (filterStaff !== "ALL") {
      const items = Array.isArray(inv.items) ? inv.items : [];
      if (!items.some((it) => String(it.staffId || "") === filterStaff)) return false;
    }
    if (!inv.invoiceDate) return !filterDate;
    if (filterDate) {
      const invDate = new Date(inv.invoiceDate).toISOString().slice(0, 10);
      if (invDate !== filterDate) return false;
    } else if (filterMonth) {
      const d = new Date(inv.invoiceDate);
      const invMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (invMonth !== filterMonth) return false;
    }
    return true;
  });

  // Newest invoices first (by invoiceDate, DESC).
  const sortedRows = [...filteredRows].sort((a, b) => {
    const ta = a.invoiceDate ? new Date(a.invoiceDate).getTime() : -Infinity;
    const tb = b.invoiceDate ? new Date(b.invoiceDate).getTime() : -Infinity;
    return tb - ta;
  });

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedRows = sortedRows.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  // Reset to the first page whenever the filters change the result set.
  useEffect(() => { setPage(0); }, [filterCustomer, filterStaff, filterDate, filterMonth, pageSize]);

  const startEdit = (inv) => {
    const items = (Array.isArray(inv.items) ? inv.items : []).map((it) => ({
      id: it.id,
      serviceId: it.serviceId || "",
      serviceName: it.serviceName || it.productName || "",
      staffId: it.staffId || "",
      staffName: it.staffName || it.staffFirstName || "",
      quantity: it.quantity || 0,
      unitPrice: it.unitPrice || 0,
    }));
    setEditItems(items);
    setOriginalItems(JSON.parse(JSON.stringify(items)));
    setEditStatus(inv.status || "");
    const calcSub = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unitPrice || 0), 0);
    setEditSubtotal(inv.subtotal ?? calcSub);
    setEditGrandTotal(inv.grandTotal || 0);
    setEditDiscount(inv.discountAmount || 0);
    setEditingId(inv.id);
    setExpanded((e) => ({ ...e, [inv.id]: true }));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditItems([]);
    setOriginalItems([]);
    setEditStatus("");
    setEditGrandTotal("");
    setEditSubtotal("");
    setEditDiscount("");
  };

  const updateEditItem = (i, patch) => {
    const n = [...editItems];
    n[i] = { ...n[i], ...patch };
    setEditItems(n);
    // Auto-recalculate subtotal and grand total
    const newSub = n.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unitPrice || 0), 0);
    setEditSubtotal(newSub.toFixed(2));
    const newGrand = newSub + newSub * 0.025 + newSub * 0.025 - Number(editDiscount || 0);
    setEditGrandTotal(newGrand.toFixed(2));
  };

  const pickEditService = (i, sid) => {
    const svc = services.find((s) => String(s.id) === String(sid));
    if (svc) updateEditItem(i, { serviceId: String(svc.id), serviceName: svc.name, unitPrice: svc.price });
  };

  const pickEditStaff = (i, sid) => {
    const s = staff.find((x) => String(x.id) === String(sid));
    if (s) updateEditItem(i, { staffId: String(s.id), staffName: [s.firstName, s.lastName].filter(Boolean).join(" ") || s.name || "" });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Only send changed items
      const changedItems = editItems.filter((item, i) => {
        const orig = originalItems[i];
        if (!orig) return true;
        return (
          String(item.serviceId) !== String(orig.serviceId) ||
          String(item.staffId) !== String(orig.staffId) ||
          Number(item.unitPrice) !== Number(orig.unitPrice)
        );
      }).map((item) => ({
        id: item.id,
        serviceId: item.serviceId,
        staffId: item.staffId,
        unitPrice: Math.round(Number(item.unitPrice) * 100) / 100,
        quantity: Number(item.quantity),
      }));

      await api.put(`/invoices/${editingId}`, {
        status: editStatus,
        grandTotal: Math.round(Number(editGrandTotal) * 100) / 100,
        subtotal: Math.round(Number(editSubtotal) * 100) / 100,
        discountAmount: Math.round(Number(editDiscount || 0) * 100) / 100,
        items: changedItems.length > 0 ? changedItems : undefined,
      });
      toast.success("Invoice updated");
      cancelEdit();
      load();
    } catch (e) {
      const data = e.response?.data;
      const msg = data?.detail || data?.message || data?.error || "Failed to update invoice";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  // When filtering by a specific staff, show only that staff's portion of the invoice
  // (sum of their line items), otherwise show the full invoice grand total.
  const displayTotal = (inv) => {
    if (filterStaff === "ALL") return Number(inv.grandTotal || 0);
    const items = Array.isArray(inv.items) ? inv.items : [];
    return items
      .filter((it) => String(it.staffId || "") === filterStaff)
      .reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unitPrice || 0), 0);
  };

  // Amount of an invoice that was settled from the wallet.
  const walletPaid = (inv) => {
    const payments = Array.isArray(inv.payments) ? inv.payments : [];
    return payments
      .filter((p) => String(p.paymentMethod || "").toUpperCase() === "WALLET")
      .reduce((s, p) => s + Number(p.amount || 0), 0);
  };

  // Header aggregate excludes wallet-paid amounts.
  const aggregateTotal = filteredRows.reduce((s, inv) => s + Math.max(0, displayTotal(inv) - walletPaid(inv)), 0);

  const markPaid = async (id) => {
    try { await api.put(`/invoices/${id}/finalize`); toast.success("Marked paid"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Billing</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
            <ReceiptText className="w-8 h-8 text-cyan-500" /> Invoices <span className="font-normal text-gold">{money(aggregateTotal)}</span>
            <span className="font-normal text-gold ml-2">({filteredRows.length})</span>
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search by customer name…"
          value={filterCustomer}
          onChange={(e) => setFilterCustomer(e.target.value)}
          className="bg-emerald-surface border-gold-subtle text-white h-10 w-64"
          data-testid="invoice-customer-search"
        />
        <Select value={filterStaff} onValueChange={setFilterStaff}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10 w-52" data-testid="invoice-staff-filter">
            <SelectValue placeholder="All Staff" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Staff</SelectItem>
            {staff.map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {[s.firstName, s.lastName].filter(Boolean).join(" ") || s.name || `Staff #${s.id}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="bg-emerald-surface border-gold-subtle text-white h-10 w-44"
          data-testid="invoice-date-search"
        />
        <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
          className="h-10 px-3 rounded-md bg-emerald-surface border border-gold-subtle text-white text-sm focus:outline-none"
          data-testid="invoice-month-filter" />
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Invoice #</TableHead>
              <TableHead className="th-gold">Customer</TableHead>
              <TableHead className="th-gold">Date</TableHead>
              <TableHead className="th-gold w-32 text-center">Total</TableHead>
              <TableHead className="th-gold w-28 text-center">Status</TableHead>
              {canEdit && <TableHead className="th-gold w-28 text-center">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 && <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="text-center text-emerald-muted py-8">No invoices yet.</TableCell></TableRow>}
            {pagedRows.map((inv) => {
              const isOpen = !!expanded[inv.id];
              const isEditing = editingId === inv.id;
              const lineItems = isEditing ? editItems : (Array.isArray(inv.items) ? inv.items : []);
              const payments = Array.isArray(inv.payments) ? inv.payments : [];
              const sub = isEditing
                ? Number(editSubtotal)
                : (inv.subtotal ?? lineItems.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unitPrice || 0), 0));
              const isProductOnly = inv.invoiceType === "PRODUCT_SALE" || (lineItems.length > 0 && lineItems.every((it) => !it.serviceId && !it.serviceName));
              const sgst = isProductOnly ? 0 : sub * 0.025;
              const cgst = isProductOnly ? 0 : sub * 0.025;
              const disc = isEditing ? Number(editDiscount || 0) : Number(inv.discountAmount || 0);
              const grand = isEditing ? Number(editGrandTotal) : Number(inv.grandTotal || 0);
              return (
                <Fragment key={inv.id}>
                  <TableRow className="border-gold-subtle cursor-pointer hover:bg-emerald-elev/40"
                      data-testid={`invoice-row-${inv.id}`}
                      onClick={() => toggle(inv.id)}>
                      <TableCell className="text-white">
                        <div className="inline-flex items-center gap-2">
                          <button type="button" aria-label={isOpen ? "Collapse" : "Expand"}
                            data-testid={`invoice-toggle-${inv.id}`}
                            className="p-1 -ml-1 rounded text-emerald-muted hover:text-white hover:bg-emerald-elev/60"
                            onClick={(e) => { e.stopPropagation(); toggle(inv.id); }}>
                            <ExpandIcon expanded={isOpen} />
                          </button>
                          <span>{inv.invoiceNo}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-emerald-muted">
                        <div className="flex flex-col">
                          <span>{inv.customerName || "—"}</span>
                          {(inv.notOnWhatsApp || inv.customerNotOnWhatsApp) && (
                            <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium mt-0.5" data-testid={`invoice-no-whatsapp-${inv.id}`}>
                              <MessageSquareOff className="w-3 h-3" /> customer not on whats app
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-emerald-muted">{formatDateTime(inv.invoiceDate)}</TableCell>
                      <TableCell className="w-32 text-center text-gold tabular-nums">{money(displayTotal(inv))}</TableCell>
                      <TableCell className="w-28 text-center">
                        <div className="flex justify-center">
                          <Badge variant="outline" className="border-green-800 text-green-300 bg-[rgba(46,125,50,0.15)]">{inv.status}</Badge>
                        </div>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="w-28 text-center" onClick={(e) => e.stopPropagation()}>
                          {!isEditing && (
                            <button onClick={() => startEdit(inv)} data-testid={`edit-invoice-${inv.id}`}
                              className="text-xs text-gold hover:underline inline-flex items-center gap-1">
                              <Edit3 className="w-3 h-3" /> Edit
                            </button>
                          )}
                        </TableCell>
                      )}
                  </TableRow>
                  {isOpen && (
                    <TableRow className="border-gold-subtle hover:bg-transparent bg-emerald-elev/20"
                      data-testid={`invoice-details-${inv.id}`}>
                      <TableCell colSpan={canEdit ? 6 : 5} className="p-0">
                        <div className="p-6 space-y-6">
                          {/* Status & Grand Total edit */}
                          {isEditing && (
                            <div className="flex items-center gap-4 flex-wrap">
                              <div>
                                <span className="text-xs text-emerald-muted uppercase tracking-widest mr-2">Status</span>
                                <Select value={editStatus} onValueChange={setEditStatus}>
                                  <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-9 w-44 inline-flex">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-emerald-surface border-gold-subtle text-white">
                                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600 uppercase">Grand Total</span>
                                <Input type="number" step="0.01" value={editGrandTotal}
                                  onChange={(e) => setEditGrandTotal(e.target.value)}
                                  className="bg-emerald-elev border-gold-subtle text-gold h-9 w-36 inline-flex" />
                              </div>
                              <div className="ml-auto flex gap-2">
                                <button onClick={saveEdit} disabled={saving}
                                  className="gold-button px-4 py-1.5 rounded-md text-sm inline-flex items-center gap-1 disabled:opacity-60">
                                  <Check className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                                </button>
                                <button onClick={cancelEdit}
                                  className="gold-outline-button px-4 py-1.5 rounded-md text-sm inline-flex items-center gap-1">
                                  <X className="w-3 h-3" /> Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Line items */}
                          <div>
                            <div className="overline mb-3">Line Items</div>
                            <div className="rounded-xl border border-gold-subtle overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-gold-subtle hover:bg-transparent bg-emerald-surface">
                                    <TableHead className="th-gold">Item</TableHead>
                                    <TableHead className="th-gold">Staff</TableHead>
                                    <TableHead className="th-gold text-right">Qty</TableHead>
                                    <TableHead className="th-gold text-right">Price</TableHead>
                                    <TableHead className="th-gold text-right">Line Total</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {lineItems.length === 0 && (
                                    <TableRow className="border-gold-subtle">
                                      <TableCell colSpan={5} className="text-center text-emerald-muted py-4">No items.</TableCell>
                                    </TableRow>
                                  )}
                                  {lineItems.map((it, i) => {
                                    const qty = Number(it.quantity || 0);
                                    const price = Number(it.unitPrice || 0);
                                    const lineTotal = qty * price;
                                    return (
                                      <TableRow key={i} className="border-gold-subtle" data-testid={`invoice-line-${inv.id}-${i}`}>
                                        {isEditing ? (
                                          <>
                                            <TableCell>
                                              <Select value={String(it.serviceId || "")} onValueChange={(v) => pickEditService(i, v)}>
                                                <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-9 w-56">
                                                  <SelectValue placeholder="Service">{it.serviceName || "Select"}</SelectValue>
                                                </SelectTrigger>
                                                <SelectContent className="bg-emerald-surface border-gold-subtle text-white max-h-60">
                                                  {services.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                                                </SelectContent>
                                              </Select>
                                            </TableCell>
                                            <TableCell>
                                              <Select value={String(it.staffId || "")} onValueChange={(v) => pickEditStaff(i, v)}>
                                                <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-9 w-36">
                                                  <SelectValue placeholder="Stylist">{it.staffName || "Select"}</SelectValue>
                                                </SelectTrigger>
                                                <SelectContent className="bg-emerald-surface border-gold-subtle text-white max-h-60">
                                                  {staff.map((s) => <SelectItem key={s.id} value={String(s.id)}>{[s.firstName, s.lastName].filter(Boolean).join(" ") || s.name}</SelectItem>)}
                                                </SelectContent>
                                              </Select>
                                            </TableCell>
                                            <TableCell className="text-right text-emerald-muted">{qty}</TableCell>
                                            <TableCell className="text-right">
                                              <Input type="number" step="0.01" value={it.unitPrice}
                                                onChange={(e) => updateEditItem(i, { unitPrice: e.target.value })}
                                                className="bg-emerald-elev border-gold-subtle text-white h-9 w-24 text-right ml-auto" />
                                            </TableCell>
                                            <TableCell className="text-right text-gold">{money(lineTotal)}</TableCell>
                                          </>
                                        ) : (
                                          <>
                                            <TableCell className="text-white">{it.serviceName || it.productName || "—"}</TableCell>
                                            <TableCell className="text-emerald-muted">{it.staffName || it.staffFirstName || it.staff_name || "—"}</TableCell>
                                            <TableCell className="text-right text-emerald-muted">{qty}</TableCell>
                                            <TableCell className="text-right text-emerald-muted">{money(price)}</TableCell>
                                            <TableCell className="text-right text-gold">{money(lineTotal)}</TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Tax & Totals breakup */}
                            <div>
                              <div className="overline mb-3">Tax & Totals</div>
                              <div className="rounded-xl border border-gold-subtle bg-emerald-surface p-4 space-y-2 text-sm">
                                <div className="flex justify-between items-center text-emerald-muted" data-testid={`invoice-subtotal-${inv.id}`}>
                                  <span>Subtotal</span>
                                  {isEditing ? (
                                    <Input type="number" step="0.01" value={editSubtotal}
                                      onChange={(e) => {
                                        setEditSubtotal(e.target.value);
                                        const newSub = Number(e.target.value || 0);
                                        const newGrand = newSub + newSub * 0.025 + newSub * 0.025 - Number(editDiscount || 0);
                                        setEditGrandTotal(newGrand.toFixed(2));
                                      }}
                                      className="bg-emerald-elev border-gold-subtle text-white h-8 w-28 text-right text-sm" />
                                  ) : (
                                    <span>{money(sub)}</span>
                                  )}
                                </div>
                                {!isProductOnly && (
                                  <>
                                    <div className="flex justify-between text-emerald-muted text-xs" data-testid={`invoice-sgst-${inv.id}`}>
                                      <span>SGST(2.5%)</span><span>{money(sgst)}</span>
                                    </div>
                                    <div className="flex justify-between text-emerald-muted text-xs" data-testid={`invoice-cgst-${inv.id}`}>
                                      <span>CGST(2.5%)</span><span>{money(cgst)}</span>
                                    </div>
                                  </>
                                )}
                                <div className="flex justify-between items-center text-emerald-muted" data-testid={`invoice-discount-${inv.id}`}>
                                  <span>Discount</span>
                                  {isEditing ? (
                                    <Input type="number" step="0.01" value={editDiscount}
                                      onChange={(e) => {
                                        setEditDiscount(e.target.value);
                                        const newSub = Number(editSubtotal || 0);
                                        const newGrand = newSub + newSub * 0.025 + newSub * 0.025 - Number(e.target.value || 0);
                                        setEditGrandTotal(newGrand.toFixed(2));
                                      }}
                                      className="bg-emerald-elev border-gold-subtle text-white h-8 w-28 text-right text-sm" />
                                  ) : (
                                    <span>-{money(disc)}</span>
                                  )}
                                </div>
                                <div className="border-t border-gold-subtle pt-2 flex justify-between items-center"
                                  data-testid={`invoice-grand-${inv.id}`}>
                                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-muted">Grand Total</span>
                                  <span className="text-xl font-bold text-gold text-right tabular-nums">{money(grand)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Payments */}
                            <div>
                              <div className="overline mb-3">Payments</div>
                              <div className="rounded-xl border border-gold-subtle bg-emerald-surface p-4 space-y-2 text-sm">
                                {payments.length === 0 && (
                                  <div className="text-emerald-muted text-center py-2">
                                    {inv.paymentMethod ? `Single payment · ${inv.paymentMethod}` : "No payment records."}
                                  </div>
                                )}
                                {payments.map((p, i) => (
                                  <div key={i} className="flex items-center justify-between border-b border-gold-subtle last:border-b-0 pb-2 last:pb-0"
                                    data-testid={`invoice-payment-${inv.id}-${i}`}>
                                    <div className="flex flex-col">
                                      <span className="text-white">{p.paymentMethod || "—"}</span>
                                      {p.referenceNumber && <span className="text-xs text-emerald-muted">Ref: {p.referenceNumber}</span>}
                                    </div>
                                    <span className="text-gold">{money(p.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        {sortedRows.length > 0 && (
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            total={sortedRows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>
    </div>
  );
}
